import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAuthScreenChrome } from "@/hooks/useAuthScreenChrome";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import { pickLogo } from "@/domain/brand_logo";
import { BrandLogo } from "@/components/ui/BrandLogo";
import { LegalLinks } from "@/components/ui/LegalLinks";
import { AuthError } from "@/services/auth.service";
import { hangulToQwerty } from "@/utils/hangulToQwerty";
import { DeviceLimitPanel } from "@/components/account/DeviceLimitPanel";
import { currentDeviceInfo, LOGOUT_NOTICE_TEXT } from "@/domain/device_info";
import { clearLogoutNotice, peekLogoutNotice, readAutoLoginOffHint } from "@/lib/sessionLocal";
import type { DeviceDoc } from "@/domain/session";
import type { DeviceInfo } from "@/domain/device_info";
import "@/components/account/account.css";

const ERROR_MESSAGE: Record<string, string> = {
  INVALID_CREDENTIALS: "아이디 또는 비밀번호가 올바르지 않습니다.",
  ACCOUNT_DISABLED: "비활성화된 계정입니다. 관리자에게 문의하세요.",
  PENDING_APPROVAL: "관리자 승인 대기 중인 계정입니다.",
  TOO_MANY_ATTEMPTS: "로그인 시도가 너무 많습니다. 잠시 후 다시 시도하세요.",
};

const ID_ALLOW = /[^a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~ ]/g;

/**
 * 로그인 화면.
 *  - `/login` 주소(세션 만료 `?reason=expired`·로그아웃 후): 로그인되면 `?next=` 또는 `/` 로 이동.
 *    `/` 는 SpaceIndexRedirect 가 첫 화면 설정(내 계정·환경 › 첫 화면)으로 보낸다(2026-11-02).
 *  - `inline`(RequireAuth 가 미로그인 상태에서 지금 주소에 그릴 때): 이동하지 않는다 — 로그인되면 RequireAuth 가
 *    같은 자리에서 앱 화면(Outlet)을 열어 주소가 그대로 이어진다. 주소창에 `?next=` 가 붙지 않는다.
 */
export function Login({ inline = false }: { inline?: boolean } = {}) {
  useAuthScreenChrome();
  const { user, loading, login, resolveDeviceLimit, cancelLogin } = useAuth();
  const { info } = useCompanyInfo();
  const loginLogo = pickLogo(info.logos, "full", "dark"); // 파란 배경 위(카드 밖) — 다크. SVG 면 글자는 currentColor 로 흰색
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const reason = params.get("reason");

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>(
    reason === "expired" ? "세션이 만료되었습니다. 다시 로그인하세요." : "",
  );
  // 12_통합관리_계정기기설계 §7·§8-E — 공용 PC 체크(PC 만), 기기 3대 가득 단계, 앱이 로그아웃시킨 이유 안내
  const [device] = useState(currentDeviceInfo);
  const [sharedPc, setSharedPc] = useState(false);
  // 입력한 아이디가 이 기기에서 자동 로그인을 꺼 둔 사람일 때만 안내(브라우저 공용 힌트가 다른 사람에게 보이지 않게)
  const autoLoginOff = readAutoLoginOffHint({ loginId });
  const [notice] = useState(() => {
    const k = peekLogoutNotice();
    return k ? LOGOUT_NOTICE_TEXT[k] : "";
  });
  useEffect(() => clearLogoutNotice(), []);
  const [limit, setLimit] = useState<{ devices: DeviceDoc[]; thisDevice: DeviceInfo } | null>(null);

  // 이미 로그인된 상태로 /login 에 오면 돌려보낸다(inline 이면 RequireAuth 가 처리)
  useEffect(() => {
    if (!inline && !loading && user) navigate(next, { replace: true });
  }, [inline, loading, user, next, navigate]);
  const goNext = () => {
    if (!inline) navigate(next, { replace: true });
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg("");
    setSubmitting(true);
    try {
      const out = await login(loginId.trim(), password, { sharedPc: device.kind === "pc" && sharedPc });
      if (out.status === "device_limit") {
        setLimit({ devices: out.devices, thisDevice: out.thisDevice });
        setSubmitting(false);
        return;
      }
      goNext();
    } catch (err) {
      const code = err instanceof AuthError ? err.code : "UNKNOWN";
      setErrorMsg(ERROR_MESSAGE[code] ?? `로그인 실패: ${(err as Error).message}`);
      setSubmitting(false);
    }
  }

  return (
    <div className="login-body">
      {loginLogo ? (
        <div className="login-logo">
          <BrandLogo src={loginLogo} alt={info.companyName || "회사 로고"} />
        </div>
      ) : null}
      <main className="login-card">
        {loginLogo ? null : (
          <div className="login-brand">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M22 20H2v-7l7-4v4l7-4v4l5-3z" />
              <path d="M5 20v-3h2v3M10 20v-3h2v3M15 20v-3h2v3" />
            </svg>{" "}
            통합관리시스템
          </div>
        )}

        {limit ? (
          <DeviceLimitPanel
            devices={limit.devices}
            thisDevice={limit.thisDevice}
            onRelease={async (id) => {
              await resolveDeviceLimit(id);
              goNext();
            }}
            onCancel={async () => {
              await cancelLogin();
              setLimit(null);
              setPassword("");
            }}
          />
        ) : (
        <form className="login-form" autoComplete="off" onSubmit={onSubmit}>
          {notice && <div className="ma-login-notice" role="status">{notice}</div>}
          <label className="field">
            <span>아이디</span>
            <input
              type="text"
              value={loginId}
              onChange={(e) => setLoginId(hangulToQwerty(e.target.value).replace(ID_ALLOW, ""))}
              required
              autoComplete="username"
              lang="en"
              inputMode="email"
              autoFocus
            />
          </label>
          <label className="field">
            <span>비밀번호</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </label>
          {device.kind === "pc" && (
            <div className="ma-login-shared">
              <label className="ma-login-check">
                <input type="checkbox" checked={sharedPc} onChange={(e) => setSharedPc(e.target.checked)} />
                공용 PC입니다
              </label>
              <p className="ma-help">체크하면 창을 닫을 때 로그아웃되고, 기기로 등록하지 않습니다</p>
            </div>
          )}
          {autoLoginOff && !sharedPc && (
            <p className="ma-help">이 기기는 자동 로그인을 꺼 두었습니다 — 창을 닫으면 로그아웃됩니다(기기관리에서 다시 켤 수 있음)</p>
          )}
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "로그인 중..." : "로그인"}
          </button>
          <div className="login-msg" role="alert">
            {errorMsg}
          </div>
        </form>
        )}

        <div className="login-alt">
          <span className="login-alt-q">계정이 없으신가요?</span>
          <Link to="/signup">회원가입</Link>
        </div>

        <div className="login-foot">
          중앙산업(주) · 경원산업(주)
          <br />
          대표번호 : 061-351-9500 (담당자 정의관)
          <LegalLinks />
        </div>
      </main>
    </div>
  );
}
